import { createContext, useContext, useLayoutEffect, useRef, type MutableRefObject } from 'react';

export interface ModalDraft {
  dirty: boolean;
  busy: boolean;
  form: HTMLFormElement | null;
  save?: () => void;
}
export const ModalDraftContext = createContext<{ draft: MutableRefObject<ModalDraft | null>; settled: (saved: boolean) => void; setDismissLocked: (locked: boolean) => void } | null>(null);

/** Explicit confirmation screens must resolve through their own action buttons. */
export function useModalDismissLock(locked: boolean) {
  const context = useContext(ModalDraftContext);
  useLayoutEffect(() => {
    context?.setDismissLocked(locked);
    return () => context?.setDismissLocked(false);
  }, [context, locked]);
}

/** Compare editable state, rather than DOM fields, so hidden wizard steps remain protected. */
export function useModalDraft(value: unknown, busy: boolean, save?: () => void) {
  const context = useContext(ModalDraftContext);
  const baseline = useRef(JSON.stringify(value));
  const formRef = useRef<HTMLFormElement>(null);
  useLayoutEffect(() => {
    if (context) context.draft.current = { dirty: JSON.stringify(value) !== baseline.current, busy, form: formRef.current, save };
  });
  useLayoutEffect(() => () => { if (context) context.draft.current = null; }, [context]);
  return { formRef, settled: (saved: boolean) => context?.settled(saved) };
}
